import {utf8ByteLength} from "core/validation/utf8ByteLength";

export const WITHDRAWAL_SUBMISSION_SUBJECT = "EU withdrawal declaration";

/**
 * The declarant/contract fields the statutory declaration names — art. 11^1 alin. (4)(a)-(b) OUG
 * 34/2014, Anlage 2 zu Art. 246a § 1 Abs. 2 S. 1 Nr. 1 EGBGB. The three the consumer must be able to
 * "easily provide or confirm".
 */
export interface WithdrawalDeclarant {
	/** alin. (4)(a) "numele său" — editable, prefilled from the account's profile name. */
	name: string;
	/** Half of alin. (4)(b)'s "details identifying the contract" — the account's own address, which is
	 *  ALSO alin. (4)(c)'s "electronic means by which the confirmation... will be sent" (the api always
	 *  acknowledges to this same address; see `WithdrawalAcknowledgement` on the api side). Read-only:
	 *  displayed so the consumer can confirm it, never redirected from this form. */
	account: string;
	/** The other half of alin. (4)(b) — the plan the account is contracted to. Read-only. */
	plan: string;
}

/**
 * The TRANSMITTED declaration copy for the buyer's own locale — `account.withdrawal.transmitted` in
 * the catalogue.
 *
 * The declaration is the buyer's STATUTORY STATEMENT, so it has to be in the buyer's language
 * (kitecorp/transferify-ui#2585): a German buyer who presses „Widerrufen und Geld zurück“ must not be
 * recorded as having said something in English, neither in the support inbox nor in the acknowledgement
 * email that quotes it back to them. That replaces the original one-language decision
 * (kitecorp/transferify-ui#255), which predates the German market.
 *
 * `declaration` renders the statement AND the declarant lines as ONE unit per locale, rather than a
 * sentence this module then pads with labelled lines, because the model forms do not share a shape:
 * Anlage 2 EGBGB and the OUG 34/2014 Anexa partea B both NAME the service inside the sentence, where
 * the English short form keeps a separate `Plan:` line. Composing the block here from per-line
 * fragments would impose one statute's shape on the others.
 *
 * The dialog's label changed in kitecorp/transferify-ui#3698 ("Cancel and get a refund"); this statement
 * did not. It is the same declaration, transmitted the same way, and the optional reason the dialog asks
 * for travels beside it as its own request fields, never inside the statement.
 */
export interface WithdrawalTransmittedCopy {
	declaration: (declarant: WithdrawalDeclarant) => string;
}

/**
 * Generous, private caps on the one field this form lets the consumer type freely — their name. The
 * account's own name is prefilled, but art. 11^1 alin. (4)(a) OUG 34/2014 lets the consumer "furnizeze"
 * (provide) it, so it must stay editable rather than a locked read-out. At 300 characters the whole
 * declaration stays far inside the support api's 2000-byte message bound in every locale.
 */
export const MAX_WITHDRAWAL_NAME_LENGTH = 300;
export const MAX_WITHDRAWAL_NAME_BYTES = 300;

/**
 * Admits one complete Unicode code point at a time until either bound is reached — never splits a
 * multi-byte character or a surrogate pair, unlike a naive `slice`/`substring`.
 */
function limitUtf8(value: string, maxBytes: number, maxLength: number): string {
	let admitted = "";
	let bytes = 0;
	let characters = 0;
	for (const codePoint of value) {
		const codePointBytes = utf8ByteLength(codePoint);
		if (bytes + codePointBytes > maxBytes || characters + codePoint.length > maxLength) {
			break;
		}
		admitted += codePoint;
		bytes += codePointBytes;
		characters += codePoint.length;
	}
	return admitted;
}

/** Clamps the editable declarant-name field to `MAX_WITHDRAWAL_NAME_*`. */
export function limitWithdrawalName(name: string): string {
	return limitUtf8(name, MAX_WITHDRAWAL_NAME_BYTES, MAX_WITHDRAWAL_NAME_LENGTH);
}

/**
 * Whether the declaration may be confirmed: submission is permanent and needs no churn action, so only
 * a blank declarant name holds it back. The name check exists because a statutory declaration with the
 * field silently blank would carry no answer to alin. (4)(a) at all. The optional reason
 * (kitecorp/transferify-ui#3698) never holds it back: the law lets a consumer withdraw without one.
 *
 * It says nothing about a send in flight (kitecorp/transferify-ui#3079). The view DISABLES the confirm
 * button for an incomplete draft, and a disabled button drops the keyboard focus it holds out of the
 * dialog, so the in-flight state is reported separately and the button stays focusable through it; a
 * press meanwhile joins the send already in flight.
 */
export function isWithdrawalDraftComplete(name: string): boolean {
	return name.trim().length > 0;
}

/**
 * Builds the support-pipeline body for a statutory withdrawal declaration under art. 11^1 alin. (3)-(4)
 * OUG 34/2014 / § 356a BGB: the locale's model-form declaration, the declarant's name, and the
 * account/plan pair that identify the contract.
 *
 * This is also the exact text `WithdrawalAcknowledgementEmail` (transferify-api-service) echoes back to
 * the consumer as "What you sent" / „Was du gesendet hast“ — the api passes it through verbatim, which
 * is why composing it in the buyer's language HERE is the whole of the fix for
 * kitecorp/transferify-ui#2585. Carrying name/account/plan here is what makes that durable-medium
 * receipt a full record of alin. (4)'s three fields.
 *
 * The name is trimmed HERE rather than in the catalogue, so the incidental whitespace rule lives in one
 * place instead of once per locale; and the three fields are passed by name rather than as a spread, so a
 * catalogue leaf cannot reach anything its contract does not name.
 */
export function withdrawalSubmissionMessage(
	copy: WithdrawalTransmittedCopy,
	{name, account, plan}: WithdrawalDeclarant,
): string {
	return copy.declaration({name: name.trim(), account, plan});
}
